import { useState, useEffect, FC, ChangeEvent, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { getRequest, postRequest, putRequest } from '../utils/api';
import { System } from '../interfaces/system';

const SystemDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [system, setSystem] = useState<System | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchSystem = async () => {
      try {
        setError(null);
        setLoading(true);
        const data = await getRequest<System>(`/systems/${id}`);
        setSystem(data);
      } catch (err: any) {
        console.error('Error fetching system:', err);
        setError(err.message || 'Failed to fetch system details.');
      } finally {
        setLoading(false);
      }
    };

    fetchSystem();
  }, [id]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!system) return;
    const { name, value, type, checked } = e.target;
    setSystem({
      ...system,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!system) return;

    try {
      setError(null);
      setSuccessMessage(null);
      await putRequest(`/systems/${id}`, system);
      setSuccessMessage('System details updated successfully.');
    } catch (err: any) {
      console.error('Error updating system:', err);
      setError(err.message || 'Failed to update system.');
    }
  };

  const handleCopyToken = () => {
    if (system) {
      navigator.clipboard
        .writeText(system.token)
        .then(() => {
          setSuccessMessage('Token copied to clipboard.');
        })
        .catch((err) => {
          console.error('Failed to copy token:', err);
          setError('Failed to copy token.');
        });
    }
  };

  const generateNewToken = async () => {
    if (!id) return;
    if (!system) return;

    if (!window.confirm('Are you sure you want to generate a new token?'))
      return;

    try {
      setError(null);
      const data = await postRequest(`/systems/${id}/generateNewToken`);
      setSystem({ ...system, token: data.token });
      setSuccessMessage('New token generated successfully.');
    } catch (err: any) {
      console.error('Error generating new token:', err);
      setError(err.message || 'Failed to generate new token.');
    }
  };

  if (loading) return <div>Loading...</div>;

  if (error)
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );

  if (!system) return <div>System not found.</div>;

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <h2 className="text-center mb-4">System Details</h2>

          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="card shadow-sm p-4">
            <div className="mb-3">
              <label className="form-label">System Name</label>
              <input
                type="text"
                name="name"
                value={system.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">ID</label>
              <input
                type="text"
                className="form-control"
                value={system.id}
                disabled
              />
            </div>

            <div className="mb-3 form-check">
              <input
                type="checkbox"
                name="enabled"
                checked={system.enabled}
                onChange={handleChange}
                className="form-check-input"
              />
              <label className="form-check-label">Enabled</label>
            </div>

            <div className="mb-3">
              <label className="form-label">Check Period (seconds)</label>
              <input
                type="number"
                name="check_period"
                value={system.check_period}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Last Health Check</label>
              <input
                type="text"
                className="form-control"
                value={system.last_health_check || 'Never'}
                disabled
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Last Health Status</label>
              <input
                type="text"
                name="last_health_status"
                value={system.last_health_status}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Dependency</label>
              <input
                type="text"
                className="form-control"
                value={
                  system.dependency
                    ? `${system.dependency.name} (ID: ${system.dependency.id})`
                    : 'None'
                }
                disabled
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Token</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={system.token}
                  disabled
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleCopyToken}
                >
                  Copy
                </button>
              </div>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={generateNewToken}
              >
                Generate New Token
              </button>
            </div>

            <button type="submit" className="btn btn-primary w-100">
              Save Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SystemDetails;
